(() => {
  const nativeSearchButton = () =>
    document.querySelector('#header-component .search-action button');

  const nativeCartButton = () =>
    document.querySelector('#header-component [data-testid="cart-drawer-trigger"]');

  // Horizon's mobile menu drawer opens by toggling the hamburger <summary>.
  // The summary is hidden on mobile (the bottom "Menu" tab replaces it), but a
  // programmatic click still toggles its parent <details>, so the drawer opens.
  const nativeMenuButton = () =>
    document.querySelector('#header-component .header__icon--menu');

  const openNativeSearch = (event) => {
    event?.preventDefault();
    nativeSearchButton()?.click();
  };

  const openNativeCart = (event) => {
    const cartButton = nativeCartButton();
    // Horizon only renders the drawer's <dialog> when the cart has items; when it
    // is empty there is nothing to open, so let the Bag tab's href="/cart" through.
    const drawerReady = document.querySelector('#cart-drawer dialog');
    if (!cartButton || !drawerReady) return;
    event.preventDefault();
    cartButton.click();
  };

  const openNativeMenu = (event) => {
    const menuButton = nativeMenuButton();
    if (!menuButton) return;
    event.preventDefault();
    menuButton.click();
  };

  // Turn the (single-message) announcement bar into a seamless scrolling ticker
  // on mobile. Two identical groups animate translateX(0 -> -50%) so the loop is
  // seamless. The native slider is kept in the DOM (visually hidden via CSS) so
  // screen readers still read the announcement and its link stays reachable; the
  // marquee itself is aria-hidden decoration. CSS shows it only <=749px.
  const installAnnouncementMarquee = () => {
    const bar = document.querySelector('.announcement-bar');
    if (!bar) return;
    if (bar.dataset.bkMarquee === 'true' && bar.querySelector('.bk-marquee-viewport')) return;

    const slider = bar.querySelector('.announcement-bar__slider');
    const message = bar.querySelector('.announcement-bar__text')?.textContent.trim();
    if (!slider || !message) return;
    const href = bar.querySelector('.announcement-bar__link')?.getAttribute('href') || null;

    bar.querySelector('.bk-marquee-viewport')?.remove();

    const makeGroup = () => {
      const group = document.createElement('div');
      group.className = 'bk-marquee__group';
      // 3 copies per group comfortably exceeds any phone width, so the seamless
      // -50% loop never shows an empty edge.
      for (let i = 0; i < 3; i += 1) {
        const item = document.createElement('span');
        item.className = 'bk-marquee__item';
        item.textContent = message;
        const sep = document.createElement('span');
        sep.className = 'bk-marquee__sep';
        sep.setAttribute('aria-hidden', 'true');
        sep.textContent = '·';
        group.append(item, sep);
      }
      return group;
    };

    const track = document.createElement('div');
    track.className = 'bk-marquee';
    const group = makeGroup();
    track.append(group, group.cloneNode(true));

    const viewport = document.createElement(href ? 'a' : 'div');
    viewport.className = 'bk-marquee-viewport';
    if (href) viewport.setAttribute('href', href);
    viewport.setAttribute('aria-hidden', 'true');
    viewport.setAttribute('tabindex', '-1');
    viewport.append(track);
    bar.append(viewport);
    bar.dataset.bkMarquee = 'true';

    // Consistent ~55px/s when the group is measurable (visible on mobile);
    // fall back to a sane default when it is built while hidden (desktop).
    const width = group.getBoundingClientRect().width;
    track.style.animationDuration = width > 0 ? `${Math.max(10, width / 55).toFixed(1)}s` : '18s';
  };

  // --- Trends: full-screen shoppable reel feed opened by the center nav tab ---
  const playReelInView = (dialog) => {
    const feed = dialog.querySelector('[data-bk-trends-feed]');
    if (!feed) return;
    const mid = feed.getBoundingClientRect().top + feed.clientHeight / 2;
    dialog.querySelectorAll('.bk-reel__video').forEach((video) => {
      const rect = video.getBoundingClientRect();
      if (rect.top < mid && rect.bottom > mid) video.play?.().catch(() => {});
      else video.pause?.();
    });
  };

  const openTrends = () => {
    const dialog = document.getElementById('bk-trends');
    if (!dialog) return;
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
    // Start on the first real reel, past the looping clone of the last one.
    const feed = dialog.querySelector('[data-bk-trends-feed]');
    const first = feed?.querySelector('.bk-reel:not([data-bk-reel-clone])');
    if (feed && first) feed.scrollTop = first.offsetTop - feed.firstElementChild.offsetTop;
    playReelInView(dialog);
    loadAutoReels(dialog);
  };

  // Auto reels (sections/bk-trends-auto.liquid) are fetched once, on first open, so the
  // site-wide dialog stays light on every other page view.
  const loadAutoReels = async (dialog) => {
    if (dialog.dataset.bkAutoReels || typeof dialog.bkAddReels !== 'function') return;
    dialog.dataset.bkAutoReels = 'loading';
    try {
      const root = window.Shopify?.routes?.root || '/';
      const data = await fetch(`${root}?sections=bk-trends-auto`).then((r) => r.json());
      const doc = new DOMParser().parseFromString(data['bk-trends-auto'] || '', 'text/html');
      dialog.bkAddReels([...doc.querySelectorAll('.bk-reel')]);
      dialog.dataset.bkAutoReels = 'done';
    } catch {
      delete dialog.dataset.bkAutoReels; // retry on next open
    }
  };

  const installTrends = () => {
    // Triggers live in the persistent bottom nav; resolve the dialog at click
    // time so a theme-editor section reload can't leave a stale reference.
    document.querySelectorAll('[data-bk-trends-trigger]').forEach((trigger) => {
      if (trigger.dataset.bkBound === 'true') return;
      trigger.dataset.bkBound = 'true';
      trigger.addEventListener('click', openTrends);
    });

    const dialog = document.getElementById('bk-trends');
    if (!dialog || dialog.dataset.bkBound === 'true') return;
    dialog.dataset.bkBound = 'true';

    const toast = dialog.querySelector('[data-bk-trends-toast]');
    let toastTimer;
    const showToast = (html) => {
      if (!toast) return;
      toast.innerHTML = html;
      toast.hidden = false;
      clearTimeout(toastTimer);
      toastTimer = setTimeout(() => { toast.hidden = true; }, 3400);
    };

    dialog.querySelectorAll('[data-bk-trends-close]').forEach((button) => {
      button.addEventListener('click', () => { if (dialog.open) dialog.close(); });
    });
    dialog.addEventListener('close', () => {
      dialog.querySelectorAll('video').forEach((video) => video.pause?.());
    });

    // Continuous feed: a clone of the last reel sits before the first and a
    // clone of the first after the last. Once scrolling settles on a clone,
    // jump silently to its real twin, so swiping past either end wraps around.
    const feed = dialog.querySelector('[data-bk-trends-feed]');
    let reels = feed ? [...feed.querySelectorAll('.bk-reel')] : [];
    let head = null;
    const looping = !window.Shopify?.designMode;
    if (reels.length > 1 && looping) {
      const makeClone = (reel) => {
        const clone = reel.cloneNode(true);
        [...clone.attributes].forEach(({ name }) => {
          if (name.startsWith('data-shopify-editor')) clone.removeAttribute(name);
        });
        clone.dataset.bkReelClone = 'true';
        clone.setAttribute('aria-hidden', 'true');
        clone.inert = true;
        return clone;
      };
      head = makeClone(reels[reels.length - 1]);
      const tail = makeClone(reels[0]);
      feed.prepend(head);
      feed.append(tail);
      dialog.bkRefreshHead = () => {
        const fresh = makeClone(reels[reels.length - 1]);
        const keep = feed.scrollTop;
        head.replaceWith(fresh);
        head = fresh;
        feed.scrollTop = keep;
      };
      dialog.bkTail = tail;

      const offset = (reel) => reel.offsetTop - head.offsetTop;
      const wrap = () => {
        const top = feed.scrollTop;
        if (Math.abs(top - offset(head)) < 2) feed.scrollTop = offset(reels[reels.length - 1]);
        else if (Math.abs(top - offset(tail)) < 2) feed.scrollTop = offset(reels[0]);
      };
      if ('onscrollend' in window) {
        feed.addEventListener('scrollend', wrap);
      } else {
        // iOS Safari has no scrollend yet; treat a short scroll pause as the end.
        let settleTimer;
        feed.addEventListener('scroll', () => {
          clearTimeout(settleTimer);
          settleTimer = setTimeout(wrap, 120);
        }, { passive: true });
      }
    }

    // Autoplay only the reel in view (muted); pause the rest.
    let observer = null;
    if (feed && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector('.bk-reel__video');
            if (!video) return;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) video.play?.().catch(() => {});
            else video.pause?.();
          });
        },
        { root: feed, threshold: [0, 0.6, 1] }
      );
      dialog.querySelectorAll('.bk-reel').forEach((reel) => observer.observe(reel));
    }

    // Share / Add are delegated on the dialog so lazily appended reels work too.
    dialog.addEventListener('click', async (event) => {
      const share = event.target.closest('[data-bk-share]');
      if (share) {
        const url = share.dataset.url;
        const title = share.dataset.title || document.title;
        if (navigator.share) {
          try { await navigator.share({ title, url }); } catch { /* dismissed */ }
          return;
        }
        try { await navigator.clipboard.writeText(url); showToast('Link copied'); }
        catch { window.open(url, '_blank', 'noopener'); }
        return;
      }
      // Add -> one-tap for single-variant items; sized items open the product page.
      const button = event.target.closest('[data-bk-reel-add]');
      if (!button) return;
      if (button.dataset.soldout === 'true') { showToast('Sold out'); return; }
      if (button.dataset.hasOptions === 'true' || !button.dataset.variant) {
        window.location.assign(button.dataset.url);
        return;
      }
      button.disabled = true;
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: Number(button.dataset.variant), quantity: 1 }),
        });
        if (!response.ok) throw new Error('add failed');
        const cart = await fetch('/cart.js').then((r) => r.json());
        document.querySelectorAll('[data-bk-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.toggleAttribute('hidden', !cart.item_count);
        });
        showToast('Added to bag · <a href="/cart">View bag</a>');
      } catch {
        showToast('Couldn’t add — <a href="' + button.dataset.url + '">open product</a>');
      } finally {
        button.disabled = false;
      }
    });

    // Append reels (auto reels from the Section Rendering API): skip products already in the
    // feed, insert before the looping tail clone, then refresh the head clone + observers.
    dialog.bkAddReels = (nodes) => {
      if (!feed) return;
      const have = new Set(reels.map((r) => r.querySelector('[data-bk-save]')?.dataset.handle).filter(Boolean));
      const fresh = nodes.filter((node) => {
        const handle = node.querySelector('[data-bk-save]')?.dataset.handle;
        if (!handle || have.has(handle)) return false;
        have.add(handle);
        return true;
      }).map((node) => document.importNode(node, true));
      if (!fresh.length) return;
      const anchor = dialog.bkTail && dialog.bkTail.parentNode === feed ? dialog.bkTail : null;
      fresh.forEach((reel) => {
        feed.insertBefore(reel, anchor);
        observer?.observe(reel);
      });
      reels = reels.concat(fresh);
      dialog.bkRefreshHead?.();
      initSavedItems();
    };
  };

  const syncCartCount = () => {
    const source = document.querySelector('#header-component .cart-bubble__text-count');
    const value = (source?.textContent || '').trim();
    document.querySelectorAll('[data-bk-cart-count]').forEach((target) => {
      target.textContent = value;
      target.toggleAttribute('hidden', !value || value === '0');
    });
  };

  const syncCollectionControls = () => {
    const toggle = document.querySelector('.facets-toggle');
    const countSource = document.querySelector('.products-count-wrapper');
    if (!toggle || !countSource) return;

    const filterButton = toggle.querySelector('.facets-toggle__button');
    if (filterButton && filterButton.dataset.bkLabelUpdated !== 'true') {
      const labelNode = [...filterButton.childNodes].find(
        (node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim() === 'Filter'
      );
      if (labelNode) labelNode.textContent = ' Filter & sort ';
      filterButton.setAttribute('aria-label', 'Filter and sort products');
      filterButton.dataset.bkLabelUpdated = 'true';
    }

    const wrapper = toggle.querySelector('.facets-toggle__wrapper');
    if (!wrapper) return;
    let count = wrapper.querySelector('[data-bk-results-count]');
    if (!count) {
      count = document.createElement('span');
      count.className = 'bk-results-count';
      count.dataset.bkResultsCount = '';
      count.setAttribute('aria-live', 'polite');
      wrapper.append(count);
    }
    const nextCount = countSource.textContent.trim();
    if (count.textContent !== nextCount) count.textContent = nextCount;

    const activeSources = [...document.querySelectorAll(
      '.facets-remove facet-remove-component.facets-remove__pill[data-url]'
    )].filter((source, index, sources) =>
      sources.findIndex((candidate) => candidate.dataset.url === source.dataset.url) === index
    );
    const activeSignature = activeSources.map((source) => source.dataset.url).join('|');
    let activeFilters = document.querySelector('[data-bk-active-filters]');
    if (!activeSources.length) {
      activeFilters?.remove();
    } else {
      if (!activeFilters) {
        activeFilters = document.createElement('div');
        activeFilters.className = 'bk-active-filters';
        activeFilters.dataset.bkActiveFilters = '';
        activeFilters.setAttribute('aria-label', 'Active filters');
        toggle.after(activeFilters);
      }

      if (activeFilters.dataset.bkSignature !== activeSignature) {
        activeFilters.replaceChildren();
        activeSources.forEach((source) => {
          const labelSource = source.cloneNode(true);
          labelSource.querySelectorAll('.svg-wrapper, .visually-hidden').forEach((node) => node.remove());
          const label = labelSource.textContent.trim();
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'bk-active-filters__pill';
          button.setAttribute('aria-label', `Remove ${label} filter`);
          button.append(document.createTextNode(label));
          const icon = source.querySelector('.svg-wrapper')?.cloneNode(true);
          if (icon) button.append(icon);
          button.addEventListener('click', () => window.location.assign(source.dataset.url));
          activeFilters.append(button);
        });
        activeFilters.dataset.bkSignature = activeSignature;
      }
    }

    const results = document.querySelector('results-list');
    if (results && results.dataset.bkControlsObserved !== 'true') {
      results.dataset.bkControlsObserved = 'true';
      new MutationObserver(syncCollectionControls).observe(results, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
  };

  const installUtilityLayer = () => {
    installAnnouncementMarquee();
    installTrends();

    const header = document.querySelector('#header-component');
    if (!header) return;

    const headerRow = header.querySelector('.header__row--top');
    const searchTemplate = document.querySelector('#bk-header-search-template');
    if (headerRow && searchTemplate && !headerRow.querySelector('[data-bk-header-search]')) {
      headerRow.append(searchTemplate.content.cloneNode(true));
    }

    document.querySelectorAll('[data-bk-search-trigger]').forEach((trigger) => {
      if (trigger.dataset.bkBound === 'true') return;
      trigger.dataset.bkBound = 'true';
      trigger.addEventListener('click', openNativeSearch);
    });

    document.querySelectorAll('[data-bk-cart-trigger]').forEach((trigger) => {
      if (trigger.dataset.bkBound === 'true') return;
      trigger.dataset.bkBound = 'true';
      trigger.addEventListener('click', openNativeCart);
    });

    document.querySelectorAll('[data-bk-menu-trigger]').forEach((trigger) => {
      if (trigger.dataset.bkBound === 'true') return;
      trigger.dataset.bkBound = 'true';
      trigger.addEventListener('click', openNativeMenu);
    });

    syncCartCount();
    syncCollectionControls();
    const cartBubble = header.querySelector('.cart-bubble');
    if (cartBubble && !cartBubble.dataset.bkObserved) {
      cartBubble.dataset.bkObserved = 'true';
      new MutationObserver(syncCartCount).observe(cartBubble, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    window.dispatchEvent(new Event('resize'));
  };

  // Judge.me's zero-review widget renders a redundant "No items found" line
  // under its own "Be the first to write a review" empty state; remove just
  // that node. Reviews themselves are never touched.
  const tidyReviewsEmptyState = () => {
    const widget = document.querySelector('.jdgm-review-widget');
    if (!widget || widget.dataset.bkTidyBound === 'true') return;
    widget.dataset.bkTidyBound = 'true';
    const tidy = () => {
      widget.querySelectorAll('.jm-text').forEach((el) => {
        if (el.textContent.trim() === 'No items found') {
          (el.closest('.jm-center') || el).remove();
        }
      });
    };
    tidy();
    const observer = new MutationObserver(tidy);
    observer.observe(widget, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 15000);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tidyReviewsEmptyState, { once: true });
  } else {
    tidyReviewsEmptyState();
  }
  window.addEventListener('load', tidyReviewsEmptyState);

  // --- Saved items (device-local wishlist; no account required, nothing sent
  // to any server — a deliberate fit for discreet shoppers) ---
  const SAVED_KEY = 'bk-saved-items';

  const readSaved = () => {
    try {
      const raw = localStorage.getItem(SAVED_KEY);
      const list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list.filter((h) => typeof h === 'string') : [];
    } catch {
      return [];
    }
  };

  const writeSaved = (list) => {
    try {
      localStorage.setItem(SAVED_KEY, JSON.stringify(list));
    } catch {
      /* private mode or blocked storage: the button still toggles for this view */
    }
  };

  const syncSaveButtons = () => {
    const saved = readSaved();
    document.querySelectorAll('[data-bk-save]').forEach((button) => {
      const active = saved.includes(button.dataset.handle);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
      const label = button.querySelector('[data-bk-save-label]');
      if (label) label.textContent = active ? 'Saved' : 'Save for later';
    });
    document.querySelectorAll('[data-bk-save-view]').forEach((link) => {
      link.hidden = saved.length === 0;
    });
  };

  const initSavedItems = () => {
    document.querySelectorAll('[data-bk-save]').forEach((button) => {
      if (button.dataset.bkBound === 'true') return;
      button.dataset.bkBound = 'true';
      button.addEventListener('click', () => {
        const handle = button.dataset.handle;
        if (!handle) return;
        const saved = readSaved();
        const index = saved.indexOf(handle);
        if (index === -1) {
          saved.unshift(handle);
        } else {
          saved.splice(index, 1);
        }
        writeSaved(saved);
        syncSaveButtons();
      });
    });
    syncSaveButtons();
    renderSavedGrid();
  };

  const renderSavedGrid = async () => {
    const grid = document.querySelector('[data-bk-saved-grid]');
    if (!grid || grid.dataset.bkRendered === 'true') return;
    grid.dataset.bkRendered = 'true';
    const empty = document.querySelector('[data-bk-saved-empty]');
    const saved = readSaved();
    if (saved.length === 0) {
      if (empty) empty.hidden = false;
      return;
    }
    const money = (cents) => '$' + (cents / 100).toFixed(2);
    const cards = await Promise.all(
      saved.map(async (handle) => {
        try {
          const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
          if (!response.ok) return null;
          return await response.json();
        } catch {
          return null;
        }
      })
    );
    const valid = cards.filter(Boolean);
    if (valid.length === 0) {
      if (empty) empty.hidden = false;
      return;
    }
    for (const product of valid) {
      const card = document.createElement('div');
      card.className = 'bk-saved-card';
      const imageUrl = product.featured_image
        ? product.featured_image + (product.featured_image.includes('?') ? '&' : '?') + 'width=480'
        : '';
      const image = imageUrl ? `<img src="${imageUrl}" loading="lazy" alt="">` : '';
      card.innerHTML = `
        <a class="bk-saved-card__media" href="${product.url}">${image}</a>
        <a class="bk-saved-card__title" href="${product.url}"></a>
        <p class="bk-saved-card__price">${money(product.price)}${product.available ? '' : ' · Sold out'}</p>
        <button type="button" class="bk-saved-card__remove" data-remove-handle="${product.handle}">Remove</button>
      `;
      card.querySelector('.bk-saved-card__title').textContent = product.title;
      card.querySelector('[data-remove-handle]').addEventListener('click', () => {
        const list = readSaved().filter((h) => h !== product.handle);
        writeSaved(list);
        card.remove();
        syncSaveButtons();
        if (list.length === 0 && empty) empty.hidden = false;
      });
      grid.append(card);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSavedItems, { once: true });
  } else {
    initSavedItems();
  }
  document.addEventListener('shopify:section:load', initSavedItems);

  // Horizon's <overflow-list> measures menu items before the web fonts load,
  // so the desktop menu can wrap onto an unstyled second line ("More" ghosting
  // below the cream strip). Re-slot the items once fonts are ready.
  const reflowMenusWhenFontsReady = () => {
    if (!document.fonts || !document.fonts.ready) return;
    document.fonts.ready.then(() => {
      document.querySelectorAll('overflow-list').forEach((list) => {
        list.dispatchEvent(new CustomEvent('reflow', { detail: {} }));
      });
    });
  };
  reflowMenusWhenFontsReady();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installUtilityLayer, { once: true });
  } else {
    installUtilityLayer();
  }

  document.addEventListener('shopify:section:load', installUtilityLayer);
})();
